import { Controller } from "@hotwired/stimulus"
import { layoutMap, W, H } from "../data_map_layout"

// The admin Data model page (dashboard#data_model): DataModelMap's areas,
// nodes and edges drawn as boxes on a board to drag, pinch and zoom, with
// data moving along the flows. Clicking a box opens what it is beside the
// board -- its counts, fields, jobs' runs, connections and admin pages.
// Where the boxes go and how the lines run is data_map_layout.js.
const KINDS = { has: "Associations", flow: "Data coming in", shows: "Shown on pages", moderates: "Admin decisions" }
const MIN_ZOOM = 0.12, MAX_ZOOM = 2.5

export default class extends Controller {
  static targets = ["viewport", "world", "panel", "zoomLabel", "legend"]
  static values = { map: Object }

  connect() {
    this.view = { x: 0, y: 0, k: 1 }
    this.pointers = new Map()
    this.hidden = new Set()
    this.layout()
    this.draw()
    this.buildLegend()
    this.fitWidth()
    this.viewportTarget.addEventListener("wheel", this.onWheel, { passive: false })
    this.resizeObserver = new ResizeObserver(() => this.apply())
    this.resizeObserver.observe(this.viewportTarget)
  }

  disconnect() {
    this.viewportTarget.removeEventListener("wheel", this.onWheel)
    this.resizeObserver?.disconnect()
  }

  // ---- Where everything goes -------------------------------------------------------------------

  layout() {
    Object.assign(this, layoutMap(this.mapValue))
  }

  // ---- Drawing ---------------------------------------------------------------------------------

  draw() {
    const world = this.worldTarget
    world.replaceChildren()
    world.style.width = `${this.bounds.w}px`
    world.style.height = `${this.bounds.h}px`

    for (const a of this.areas.values()) {
      const box = el("div", "dm-area")
      Object.assign(box.style, { left: `${a.x}px`, top: `${a.y}px`, width: `${a.w}px`, height: `${a.h}px`, "--area": a.color })
      box.append(el("div", "dm-area-head", a.label))
      box.dataset.area = a.id
      world.append(box)
    }

    const svg = svgEl("svg", { class: "dm-edges", width: this.bounds.w, height: this.bounds.h })
    const defs = svgEl("defs")
    for (const kind of Object.keys(KINDS)) {
      const marker = svgEl("marker", { id: `dm-arrow-${kind}`, viewBox: "0 0 10 10", refX: 9, refY: 5, markerWidth: 5, markerHeight: 5, orient: "auto-start-reverse" })
      marker.append(svgEl("path", { d: "M0,0 L10,5 L0,10 z", class: `dm-arrow dm-arrow-${kind}` }))
      defs.append(marker)
    }
    svg.append(defs)
    this.edgeEls = this.edges.map((e, i) => {
      const from = this.nodes.get(e.from)
      const { d, mid } = this.routes[i]
      const g = svgEl("g", { class: `dm-edge dm-e-${e.kind}` })
      g.style.setProperty("--edge", e.kind === "flow" ? this.areas.get(from.area).color : "")
      g.append(svgEl("path", { d, class: "dm-edge-line", "marker-end": `url(#dm-arrow-${e.kind})` }))
      const label = svgEl("text", { x: mid.x, y: mid.y - 4, class: "dm-edge-label", "text-anchor": "middle" })
      label.textContent = e.label
      g.append(label)
      svg.append(g)
      return { edge: e, g }
    })
    world.append(svg)

    this.nodeEls = new Map()
    for (const n of this.nodes.values()) {
      const area = this.areas.get(n.area)
      const box = el("button", `dm-node dm-k-${n.kind}`)
      box.type = "button"
      Object.assign(box.style, { left: `${n.x}px`, top: `${n.y}px`, width: `${W}px`, height: `${H}px`, "--area": area.color })
      box.dataset.id = n.id
      const icon = el("i", `fas ${n.icon || "fa-circle"} dm-node-icon`)
      icon.setAttribute("aria-hidden", "true")
      const text = el("span", "dm-node-text")
      text.append(el("span", "dm-node-label", n.label))
      const sub = this.subline(n)
      if (sub) text.append(el("span", `dm-node-sub${sub.alert ? " is-alert" : ""}`, sub.text))
      box.append(icon, text)
      // Enter or Space on a focused box (a pointer's click is handled in pointerUp).
      box.addEventListener("click", e => { if (e.detail === 0) this.select(n.id, { pan: true }) })
      world.append(box)
      this.nodeEls.set(n.id, box)
    }
  }

  subline(n) {
    if (n.kind === "job") {
      const failed = n.jobs.reduce((s, j) => s + j.failed, 0)
      if (failed) return { text: `${failed} failed`, alert: true }
      return { text: n.schedule || `${n.jobs.length} job${n.jobs.length > 1 ? "s" : ""}` }
    }
    if (!n.count) return n.kind === "derived" ? { text: "worked out, not stored" } : null
    const v = n.count.value
    return { text: `${number(v)} ${n.count.label}`, alert: n.inbox && v > 0 }
  }

  buildLegend() {
    const legend = this.legendTarget
    legend.replaceChildren()
    for (const [kind, label] of Object.entries(KINDS)) {
      const b = el("button", `dm-chip dm-chip-${kind} on`)
      b.type = "button"
      b.append(el("span", "dm-chip-line"), document.createTextNode(label))
      b.addEventListener("click", () => {
        this.hidden.has(kind) ? this.hidden.delete(kind) : this.hidden.add(kind)
        b.classList.toggle("on", !this.hidden.has(kind))
        this.viewportTarget.classList.toggle(`dm-hide-${kind}`, this.hidden.has(kind))
      })
      legend.append(b)
    }
    for (const a of this.areas.values()) {
      const b = el("button", "dm-chip dm-chip-area")
      b.type = "button"
      b.style.setProperty("--area", a.color)
      b.append(el("span", "dm-chip-dot"), document.createTextNode(a.label.split(" · ")[0]))
      b.addEventListener("click", () => this.focusBox(a))
      legend.append(b)
    }
  }

  // ---- Pan and zoom ----------------------------------------------------------------------------

  apply() {
    const { x, y, k } = this.view
    this.worldTarget.style.transform = `translate(${x}px, ${y}px) scale(${k})`
    if (this.hasZoomLabelTarget) this.zoomLabelTarget.textContent = `${Math.round(k * 100)}%`
  }

  fit() { this.focusBox({ x: 0, y: 0, w: this.bounds.w, h: this.bounds.h }, 1) }

  // To start: the map's whole width, from the top, so the boxes are big enough to read (on a phone, its first two columns).
  fitWidth() {
    const r = this.viewportTarget.getBoundingClientRect(), pad = 16
    const w = r.width > 700 ? this.bounds.w : this.areas.get("jobs").x + this.areas.get("jobs").w
    const k = clamp((r.width - pad * 2) / w, MIN_ZOOM, 1)
    this.view = { k, x: r.width > 700 ? (r.width - this.bounds.w * k) / 2 : pad, y: pad }
    this.apply()
  }

  focusBox(box, max = 1.2) {
    const r = this.viewportTarget.getBoundingClientRect()
    const panelW = this.panelOpen() && r.width > 700 ? this.panelTarget.offsetWidth : 0
    const w = r.width - panelW, pad = 24
    const k = clamp(Math.min((w - pad * 2) / box.w, (r.height - pad * 2) / box.h), MIN_ZOOM, max)
    this.view = { k, x: (w - box.w * k) / 2 - box.x * k, y: (r.height - box.h * k) / 2 - box.y * k }
    this.apply()
  }

  zoomAt(px, py, factor) {
    const { x, y, k } = this.view
    const nk = clamp(k * factor, MIN_ZOOM, MAX_ZOOM)
    this.view = { k: nk, x: px - (px - x) * (nk / k), y: py - (py - y) * (nk / k) }
    this.apply()
  }

  zoomIn() { this.zoomCentre(1.25) }
  zoomOut() { this.zoomCentre(0.8) }
  zoomCentre(f) { const r = this.viewportTarget.getBoundingClientRect(); this.zoomAt(r.width / 2, r.height / 2, f) }

  // A trackpad's two-finger scroll pans; a pinch (ctrlKey) or ⌘/Ctrl + wheel zooms.
  onWheel = (e) => {
    e.preventDefault()
    const r = this.viewportTarget.getBoundingClientRect()
    if (e.ctrlKey || e.metaKey) {
      this.zoomAt(e.clientX - r.left, e.clientY - r.top, Math.exp(-e.deltaY * (e.deltaMode ? 0.05 : 0.0025) * (e.ctrlKey && !e.metaKey ? 4 : 1)))
    } else {
      this.view.x -= e.deltaX
      this.view.y -= e.deltaY
      this.apply()
    }
  }

  pointerDown(e) {
    if (e.target.closest(".dm-panel, .dm-tools")) return
    e.preventDefault() // no text selection, and focus without the page jumping to the board
    this.viewportTarget.focus({ preventScroll: true })
    this.viewportTarget.setPointerCapture(e.pointerId)
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })
    this.dragged = false
    this.downAt = { x: e.clientX, y: e.clientY }
    this.pinch = this.pointers.size === 2 ? this.pinchState() : null
  }

  pointerMove(e) {
    const p = this.pointers.get(e.pointerId)
    if (!p) return
    const dx = e.clientX - p.x, dy = e.clientY - p.y
    p.x = e.clientX; p.y = e.clientY
    if (Math.hypot(e.clientX - this.downAt.x, e.clientY - this.downAt.y) > 4) {
      this.dragged = true
      this.viewportTarget.classList.add("dm-moving")
    }
    if (this.pointers.size === 2 && this.pinch) {
      const now = this.pinchState()
      const r = this.viewportTarget.getBoundingClientRect()
      this.zoomAt(now.cx - r.left, now.cy - r.top, now.dist / this.pinch.dist)
      this.view.x += now.cx - this.pinch.cx
      this.view.y += now.cy - this.pinch.cy
      this.apply()
      this.pinch = now
    } else if (this.pointers.size === 1 && this.dragged) {
      this.view.x += dx
      this.view.y += dy
      this.apply()
    }
  }

  pointerUp(e) {
    if (!this.pointers.has(e.pointerId)) return
    this.pointers.delete(e.pointerId)
    this.pinch = this.pointers.size === 2 ? this.pinchState() : null
    if (this.pointers.size) return
    this.viewportTarget.classList.remove("dm-moving")
    if (this.dragged) return
    const node = document.elementFromPoint(e.clientX, e.clientY)?.closest(".dm-node")
    node ? this.select(node.dataset.id) : this.select(null)
  }

  pinchState() {
    const [a, b] = [...this.pointers.values()]
    return { cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, dist: Math.max(Math.hypot(a.x - b.x, a.y - b.y), 1) }
  }

  // ---- Selecting a node ------------------------------------------------------------------------

  select(id, { pan = false } = {}) {
    this.selected = id
    const vp = this.viewportTarget
    vp.classList.toggle("dm-has-selection", !!id)
    const linked = new Set(id ? [id] : [])
    for (const { edge, g } of this.edgeEls) {
      const on = id && (edge.from === id || edge.to === id)
      g.classList.toggle("is-on", !!on)
      if (on) { linked.add(edge.from); linked.add(edge.to) }
    }
    for (const [nid, box] of this.nodeEls) {
      box.classList.toggle("is-selected", nid === id)
      box.classList.toggle("is-linked", linked.has(nid) && nid !== id)
    }
    if (!id) return this.closePanel()
    this.renderPanel(this.nodes.get(id))
    if (pan) this.panTo(this.nodes.get(id))
  }

  panTo(n) {
    const r = this.viewportTarget.getBoundingClientRect()
    const panelW = r.width > 700 ? this.panelTarget.offsetWidth : 0
    const sheetH = r.width > 700 ? 0 : this.panelTarget.offsetHeight
    const { k } = this.view
    this.view.x = (r.width - panelW) / 2 - (n.x + W / 2) * k
    this.view.y = (r.height - sheetH) / 2 - (n.y + H / 2) * k
    this.apply()
  }

  panelOpen() { return this.hasPanelTarget && !this.panelTarget.hidden }

  closePanel() {
    this.panelTarget.hidden = true
    if (this.selected) this.select(null)
  }

  close(e) { e?.preventDefault(); this.select(null) }

  keydown(e) {
    if (e.key === "Escape") this.select(null)
    else if (e.target.closest("input, textarea")) return
    else if (e.key === "+" || e.key === "=") this.zoomIn()
    else if (e.key === "-") this.zoomOut()
    else if (e.key === "0") this.fit()
  }

  renderPanel(n) {
    const area = this.areas.get(n.area)
    const p = this.panelTarget
    p.replaceChildren()
    p.style.setProperty("--area", area.color)
    p.hidden = false

    const head = el("div", "dm-panel-head")
    const kind = { source: "Source", job: "Solid Queue", model: "Data", derived: "Worked out", page: n.inbox ? "Inbox" : "Page" }[n.kind]
    head.append(el("span", "dm-panel-kicker", `${area.label.split(" · ")[0]} · ${kind}`))
    const close = el("button", "dm-panel-close")
    close.type = "button"
    close.setAttribute("aria-label", "Close")
    close.innerHTML = '<i class="fas fa-xmark" aria-hidden="true"></i>'
    close.addEventListener("click", () => this.select(null))
    head.append(close)
    p.append(head)

    const title = el("h2", "dm-panel-title")
    const icon = el("i", `fas ${n.icon || "fa-circle"}`)
    icon.setAttribute("aria-hidden", "true")
    title.append(icon, document.createTextNode(n.label))
    p.append(title)
    if (n.count) {
      const c = el("div", "dm-panel-count")
      c.append(el("b", null, number(n.count.value)), el("span", null, n.count.label))
      p.append(c)
    }
    if (n.schedule) p.append(el("p", "dm-panel-schedule", `Runs ${n.schedule} (config/recurring.yml)`))
    p.append(el("p", "dm-panel-desc", n.desc))

    if (n.links?.length) {
      const links = el("div", "dm-panel-links")
      for (const l of n.links) {
        const a = el("a", "ui-btn", l.label)
        a.href = l.url
        a.dataset.turboFrame = "_top"
        links.append(a)
      }
      p.append(links)
    }

    if (n.models?.length) {
      const sec = section(p, n.models.length > 1 ? "Tables" : "Table")
      const list = el("ul", "dm-list")
      for (const m of n.models) {
        const li = el("li")
        li.append(el("code", null, m.name), el("span", "dm-dim", ` ${m.table}`), el("span", "dm-num", number(m.count)))
        list.append(li)
      }
      sec.append(list)
    }

    if (n.jobs?.length) {
      const sec = section(p, "Jobs")
      const list = el("ul", "dm-list")
      for (const j of n.jobs) {
        const li = el("li")
        const bits = []
        if (j.queued) bits.push(`${j.queued} queued`)
        if (j.failed) bits.push(`${j.failed} failed`)
        bits.push(j.last_finished ? `last ran ${ago(j.last_finished)}` : "no recent run kept")
        li.append(el("code", null, j.name), el("span", `dm-dim dm-block${j.failed ? " is-alert" : ""}`, bits.join(" · ")))
        list.append(li)
      }
      sec.append(list)
    }

    const out = this.edges.filter(e => e.from === n.id), inc = this.edges.filter(e => e.to === n.id)
    if (out.length || inc.length) {
      const sec = section(p, "Connections")
      const list = el("ul", "dm-conns")
      const add = (e, other, dir) => {
        const li = el("li")
        const b = el("button", `dm-conn dm-e-${e.kind}`)
        b.type = "button"
        b.append(el("span", "dm-conn-dir", dir), el("span", "dm-conn-label", e.label), el("span", "dm-conn-node", this.nodes.get(other).label))
        b.addEventListener("click", () => this.select(other, { pan: true }))
        li.append(b)
        list.append(li)
      }
      out.forEach(e => add(e, e.to, "→"))
      inc.forEach(e => add(e, e.from, "←"))
      sec.append(list)
    }
    if (n.fields?.length) {
      // A source's endpoints are short and open; a table's columns fold away.
      const sec = section(p, n.kind === "source" ? "Endpoints" : `Fields of ${n.models?.[0]?.name || ""} (${n.fields.length})`)
      const list = el("ul", "dm-fields")
      for (const f of n.fields) list.append(el("li", null, f))
      if (n.kind === "source") sec.append(list)
      else {
        const fold = el("details", "dm-fold")
        fold.append(el("summary", null, "Show the columns"), list)
        sec.append(fold)
      }
    }
    p.scrollTop = 0
  }
}

// ---- Helpers -----------------------------------------------------------------------------------

function el(tag, cls, text) {
  const e = document.createElement(tag)
  if (cls) e.className = cls
  if (text != null) e.textContent = text
  return e
}

function svgEl(tag, attrs = {}) {
  const e = document.createElementNS("http://www.w3.org/2000/svg", tag)
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v)
  return e
}

function section(parent, title) {
  const s = el("section", "dm-panel-sec")
  s.append(el("h3", null, title))
  parent.append(s)
  return s
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const number = v => Number(v).toLocaleString()

function ago(iso) {
  const s = (Date.now() - new Date(iso)) / 1000
  if (s < 90) return "just now"
  if (s < 3600) return `${Math.round(s / 60)} min ago`
  if (s < 86400 * 2) return `${Math.round(s / 3600)} h ago`
  return `${Math.round(s / 86400)} days ago`
}
